import React from 'react';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import {
  ConnectSettingsClinicWidgetRequestTypeFragment,
  WorkflowEventSetting,
  useSoftDeleteClinicSettingsConnectWidgetFormTemplateMutation,
  useUpdateClinicSettingsConnectWidgetMutation,
} from 'shared/types/graphql';
import * as Sentry from '@sentry/react';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import Modal from 'shared/components/Modal/Modal';
import { Button, Text, ListItem, UnorderedList, useDisclosure, Link } from '@televet/kibble-ui';
import { getClinicWidgetRequestTypeDisplayName } from 'shared/utils/widgetRequests';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useQuery } from '@apollo/client/react/hooks';
import { GET_CLINIC_WORKFLOWS_EVENT_SETTINGS } from 'shared/queries/workflows';
import { GraphQLFetchPolicies } from 'shared/enums';

export interface IDeleteWidgetRequestTypeProps {
  clinicId?: string;
  clinicWidgetRequestType?: ConnectSettingsClinicWidgetRequestTypeFragment;
  onConfirm?: () => void;
}
interface IDeleteWidgetRequestTypeModal {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteWidgetRequestTypeModal = ({
  isOpen,
  onClose,
  clinicId,
  clinicWidgetRequestType,
  onConfirm,
}: IDeleteWidgetRequestTypeModal & IDeleteWidgetRequestTypeProps): JSX.Element => {
  const [updateClinic, { loading: updatingClinic }] = useUpdateClinicSettingsConnectWidgetMutation({
    errorPolicy: GraphQLErrorPolicies.All,
  });
  const [softDeleteFormTemplate, { loading: deletingFormTemplate }] =
    useSoftDeleteClinicSettingsConnectWidgetFormTemplateMutation();
  const { toastMessagePop } = useCustomToast();

  const deleteWidgetRequest = async (): Promise<void> => {
    await updateClinic({
      variables: {
        where: {
          id: clinicId,
        },
        data: {
          clinicWidgetSetting: {
            update: {
              data: {
                clinicWidgetRequestTypes: {
                  update: [
                    {
                      where: { id: clinicWidgetRequestType?.id },
                      data: {
                        isDeleted: true,
                        formTemplate: { disconnect: true },
                      },
                    },
                  ],
                },
              },
            },
          },
        },
      },
    });
  };

  const { data: workflowEventSettings } = useQuery<{
    findManyWorkflowEventSetting: WorkflowEventSetting[];
  }>(GET_CLINIC_WORKFLOWS_EVENT_SETTINGS, {
    variables: {
      where: {
        clinic: {
          id: { equals: clinicId },
        },
      },
    },
    skip: !clinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  const onDeleteRequestOption = async (): Promise<void> => {
    try {
      await deleteWidgetRequest();
      toastMessagePop('Request option deleted');
      onConfirm && onConfirm();
      onClose();

      // Check for other entities connected to forms before prompting for deletion
      const widgetConnections = clinicWidgetRequestType?.formTemplate?.clinicWidgetRequestTypes.length || 0;
      const hasAutomationConnections = workflowEventSettings?.findManyWorkflowEventSetting?.some((setting) =>
        setting.actions.some((action) => action.config.formTemplateId === clinicWidgetRequestType?.formTemplate?.id),
      );

      if (widgetConnections <= 1 && !hasAutomationConnections) {
        onDeleteFormOpen();
      }
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toastMessagePop(
        'Whoops! An error occurred while deleting that request option. Please try again.',
        STATUSES.error,
      );
    }
  };

  const onDeleteFormTemplate = async (): Promise<void> => {
    try {
      await softDeleteFormTemplate({ variables: { where: { id: clinicWidgetRequestType?.formTemplate?.id } } });

      toastMessagePop('Request form deleted');
      onDeleteFormClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toastMessagePop('Whoops! An error occurred while deleting that request form. Please try again.', STATUSES.error);
    }
  };

  const onSaveFormTemplate = (): void => {
    toastMessagePop('Request form saved');
    onDeleteFormClose();
  };

  const { onOpen: onDeleteFormOpen, isOpen: isDeleteFormOpen, onClose: onDeleteFormClose } = useDisclosure();
  const widgetName = clinicWidgetRequestType ? getClinicWidgetRequestTypeDisplayName(clinicWidgetRequestType) : '';

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        header="Delete Request Option"
        buttons={
          <Button variant="primaryDestructive" size="md" isLoading={updatingClinic} onClick={onDeleteRequestOption}>
            Delete Request Option
          </Button>
        }
      >
        <Text>Are you sure you want to delete &apos;{widgetName}&apos;?</Text>
      </Modal>
      <Modal
        isOpen={isDeleteFormOpen}
        onClose={onDeleteFormClose}
        hasCancel={false}
        hasClose={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        header="Save Request Form?"
        buttons={
          <>
            <Button
              variant="primaryDestructive"
              size="md"
              isLoading={deletingFormTemplate}
              onClick={onDeleteFormTemplate}
            >
              Delete Form
            </Button>
            <Button size="md" onClick={onSaveFormTemplate} autoFocus>
              Save Form
            </Button>
          </>
        }
      >
        <Text>The following request form was being used in &apos;{widgetName}&apos;:</Text>
        <UnorderedList stylePosition="inside" mb={5}>
          <ListItem>
            <Link
              as={ReactRouterLink}
              fontWeight="bold"
              to={`/forms/${clinicWidgetRequestType?.formTemplate?.id}`}
              target="_blank"
            >
              {clinicWidgetRequestType?.formTemplate?.title}
            </Link>
          </ListItem>
        </UnorderedList>
        <Text>Do you want to save or delete this form?</Text>
      </Modal>
    </>
  );
};

export default DeleteWidgetRequestTypeModal;
